import React, { Component, lazy, Suspense } from "react";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";

import { type Message, type Model, type ToolApprovalDecision, type AttachmentMeta, type CompletionLogDetail, getAttachmentIconComponent, getAttachmentType } from "../models.ts";

import { PromiseCompletion } from "../classes/PromiseCompletion.ts";
import apiService from "../services/apiService.ts";
import { wsService } from "../services/wsService.ts";
import modelService from "../services/modelService.ts";
import { imageService } from "../services/imageService.ts";
import { toastService } from "../services/toastService.ts";
import { modalService } from "./Modal.tsx";

import TokensCounter from "./TokensCounter.tsx";
import ContextMenu, { ContextMenuItem } from "./ContextMenu/index.ts";
import LazyMarkdownRenderer from "./LazyMarkdownRenderer.tsx";
import { ImageWrapper } from "./ImageWrapper.tsx";
import { UploadProgressRing } from "./UploadProgressRing.tsx";
import { Timestamp } from "./Timestamp.tsx";
import { ImgCopy } from "./Images/ImgCopy.tsx";
import { ImgTrash } from "./Images/ImgTrash.tsx";
import { ImgRetry } from "./Images/ImgRetry.tsx";
import { ImgEdit } from "./Images/ImgEdit.tsx";
import { ImgShare } from "./Images/ImgShare.tsx";
import { ImgFork } from "./Images/ImgFork.tsx";
import { MessageSources } from "./MessageSources.tsx";
import { ImgTakeImage } from "./Images/ImgTakeImage.tsx";
import { ImgFileGeneric } from "./Images/ImgFileGeneric.tsx";
import { ImgSubAgent } from "./Images/ImgSubAgent.tsx";
import { ImgAttachFile } from "./Images/ImgAttachFile.tsx";
import { ImgPrevious } from "./Images/ImgPrevious.tsx";
import { ImgNext } from "./Images/ImgNext.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import { ImgMusic } from "./Images/ImgMusic.tsx";
import { ImgMoreDots } from "./Images/ImgMoreDots.tsx";
import { ImgKeyArrow } from "./Images/ImgKeyArrow.tsx";
import { resolveIconComponent } from "./ChatMessageAttachment.tsx";
import { ChatMessageProgress } from "./ChatMessageProgress.tsx";
import { ChatMessageToolApproval } from "./ChatMessageToolApproval.tsx";
import { ChatMessageModelLoadingProgress } from "./ChatMessageModelLoadingProgress.tsx";
import { CompactThinkingLog } from "./CompactThinkingLog.tsx";
import { ContextDividerCard } from "./ContextDividerCard.tsx";
import { MessageStats } from "./MessageStats.tsx";
import { MessageCost } from "./MessageCost.tsx";
const PdfAttachmentCard = lazy(() => import('./PdfAttachmentCard.tsx'));
const TextViewerDialog = lazy(() => import("../pages/Chat/TextViewerDialog"));
const CompletionLogDetailDialog = lazy(() => import("./SettingsDialog/CompletionLogDetailDialog"));
const PdfViewerDialog = lazy(() => import("../pages/Chat/PdfViewerDialog"));
const CompactedMessagesDialog = lazy(() => import("../pages/Chat/CompactedMessagesDialog"));


type ChatMessageProps = { 
  chatId?: string | null;
  isGenerating?: boolean;
  hasModel?: boolean;
  message: Message;
  isLast: boolean;
  userName?: string | null;
  userPicture?: string | null;
  userEmailMd5?: string | null;
  currentModel: Model | null;
  tools?: string[] | null;
  generatingMessageId?: string | null;
  pendingToolApprovalName?: string | null;
  toolApprovalDecision?: ToolApprovalDecision | null;

  codeHighlightingEnabled: boolean;
  showTokenUsage: boolean;
  showFullThinking?: boolean;

  onRemove?: (message: Message) => void;
  onRegenerate?: (message: Message) => void;
  onRegenerateFull?: (message: Message) => void;
  onShare?: (message: Message) => void;
  onFork?: (message: Message) => void;
  onUpdated?: (message: Message, updatedContent: string, attachmentIds: string[]) => void;
  onEditing?: (message: Message, isEditing: boolean) => void;
  onMessageSelected?: (message: Message) => void;
  onImageCancel?: (message: Message, promptId: string) => Promise<void>;
  onTakeImageSettings?: (message: Message) => void;
  onToolApprovalDecision?: (toolName: string, decision: ToolApprovalDecision) => void;
  onUseImageAttachment?: (attachmentId: string) => void;
  chatImageFileList?: string[]; // Full list of image URLs across the entire conversation
};

@observer
export class ChatMessage extends Component<ChatMessageProps> {
  @observable isEditing: boolean = false;
  @observable editedContent: string = '';
  @observable.ref _textareaRef: React.RefObject<HTMLTextAreaElement | null> = React.createRef();
  @observable.ref _previewBlobUrl: string | null = null;
  @observable _progress: { current: number, max: number } | null = null;
  @observable _queuePosition: { position: number, total: number } | null = null;
  @observable _promptId: string | null = null;
  @observable private _logExpanded: boolean = false;
  @observable _fileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable _docFileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable.ref _attachBtnRef: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable _existingImageMetas: AttachmentMeta[] = [];
  @observable _existingAttachmentMetas: AttachmentMeta[] = [];
  /** Aggregates all in-flight attachment uploads in edit mode; `isPending` gates Save. */
  @observable private _uploading: PromiseCompletion = new PromiseCompletion();
  /** Per-file placeholders for attachments still in flight (server id unknown yet).
   *  `progress` is the upload fraction (0-1) shown on the placeholder's progress ring. */
  @observable private _pendingUploads: { id: string; filename: string; isImage: boolean; progress: number }[] = [];
  /** Abort controllers for in-flight uploads, keyed by pending id (cancel-on-click). */
  private _uploadAborts = new Map<string, AbortController>();
  private _uploadSeq = 0;
  @observable _loadingImagesBase64: PromiseCompletion = new PromiseCompletion();
  @observable _executingNode: string | null = null;
  @observable.ref _moreActionsBtnRef: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable.ref _moreActionsHeaderBtnRef: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable private _isShiftPressed: boolean = false;
  @observable private _isCtrlPressed: boolean = false;
  @observable private _hasKeyboard: boolean = false;
  @observable private _attachmentMetas: Map<string, AttachmentMeta> = new Map();
  private _openCompactedDialog = async () => {
    const { message } = this.props;
    if (!message.compactedMessageIds?.length) return;
    await modalService.show(CompactedMessagesDialog, { messageIds: message.compactedMessageIds });
  };

  constructor(props: ChatMessageProps) {
    super(props);
    this._logExpanded = props.showFullThinking ?? false;
    makeObservable(this);
  }

  @action
  componentDidMount() {
    //enter edit mode for empty system message
    if (this.props.message.role === 'system' && !this.props.message.content) {
      this.isEditing = true;
      this.editedContent = '';
      setTimeout(() => {
        const textarea = this._textareaRef.current;
        if (textarea) {
          this._recalculateHeight();
          textarea.focus();
        }
      }, 0);
    }
    wsService.on('taskPreview', this._onTaskPreview);
    wsService.on('progress', this._onProgress);
    wsService.on('image_queued', this._onImageQueued);
    wsService.on('queue_position', this._onQueuePosition);
    wsService.on('interrupted', this._onImageInterrupted);
    wsService.on('executing', this._onExecuting);

    window.addEventListener('keydown', this._onGlobalKeyDown);
    window.addEventListener('keyup', this._onGlobalKeyUp);

    // Load attachment metadata for non-image attachments
    this._loadAttachmentMetas();
  }

  componentDidUpdate(prevProps: ChatMessageProps) {
    const prevIds = prevProps.message.attachmentIds || [];
    const nextIds = this.props.message.attachmentIds || [];
    // Load metas if any new attachment IDs were added (e.g. video generated after mount)
    const hasNew = nextIds.some(id => !prevIds.includes(id));
    if (hasNew) {
      this._loadAttachmentMetas();
    }
  }

  componentWillUnmount(): void {
    wsService.off('taskPreview', this._onTaskPreview);
    wsService.off('progress', this._onProgress);
    wsService.off('image_queued', this._onImageQueued);
    wsService.off('queue_position', this._onQueuePosition);
    wsService.off('interrupted', this._onImageInterrupted);
    wsService.off('executing', this._onExecuting);
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
    }

    window.removeEventListener('keydown', this._onGlobalKeyDown);
    window.removeEventListener('keyup', this._onGlobalKeyUp);
  }


  @action
  private _onGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } else if (e.key === 'Control') {
      this._isCtrlPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } 
  };

  @action
  private _onGlobalKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = false;
    } else if (e.key === 'Control') {
      this._isCtrlPressed = false;
    }
  };

  @action
  private _onProgress = (data: unknown) => {
    const progressData = data as { current: number, max: number, promptId: string };
    this._progress = progressData;
    this._promptId = progressData.promptId;
    this._queuePosition = null; // generation started — no longer waiting in queue
  }

  @action
  private _onQueuePosition = (data: unknown) => {
    if (!this.props.isLast) {
      return;
    }
    const { promptId, position, total } = data as { promptId: string, position: number, total: number };
    if (this._promptId && this._promptId !== promptId) {
      return;
    }
    this._promptId = promptId;
    this._queuePosition = { position, total };
  }

  @action.bound
  private _onImageQueued (data: unknown) {
    const { promptId } = data as { promptId: string };
    
    if (this.props.isLast) {
      const previewBlobUrl = this._previewBlobUrl;
      if (previewBlobUrl) {
        setTimeout(() => URL.revokeObjectURL(previewBlobUrl), 50);
        if (this._previewBlobUrl === previewBlobUrl) {
          this._previewBlobUrl = null;
        }
      }
      this._progress = null;
      this._executingNode = null;
      this._queuePosition = null;
    }
    this._promptId = promptId;
  }

  @action
  private _onImageInterrupted = () => {
    // Handle image interruption
    this._executingNode = null;
    this._queuePosition = null;
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
      this._previewBlobUrl = null;
    }
  }

  @action
  private _onExecuting = (data: unknown) => {
    if (!this.props.isLast) {
      return;
    }
    const { class_type } = data as { class_type: string };
    this._executingNode = class_type;
    this._queuePosition = null; // node execution started — no longer waiting in queue
  }

  // Tracks the compact thinking log's body visibility so the outer tool-progress
  // block is only shown while the log is collapsed (avoids duplicate progress UI).
  @action
  private _onLogExpandedChange = (expanded: boolean) => {
    this._logExpanded = expanded;
  }

  @action
  private _onTaskPreview = async (data: unknown) => {
    if (!this.props.isLast) {
      return;
    }

    const blob = (data as Blob).slice();
    if (!await this._isDataPreviewImageValid(blob)) {
      return;
    }

    const previewBlobUrl = this._previewBlobUrl;
    if (previewBlobUrl) {
      setTimeout(() => URL.revokeObjectURL(previewBlobUrl), 50);
    }
    this._previewBlobUrl = URL.createObjectURL(blob);
    this._executingNode = null; // Clear executing node when preview is available
    this._queuePosition = null;
  }

  /** Validates the preview payload before it can reach the <img>:
   *  1. magic header bytes (JPEG now, PNG kept for the future),
   *  2. an actual decode attempt (createImageBitmap) — a valid header alone
   *     doesn't guarantee the frame is complete, so corrupt/truncated frames
   *     are dropped here instead of failing in the "Failed to load" state.
   *  Invalid payloads are ignored with a console warning that includes the
   *  first 32 characters of the blob. */
  private async _isDataPreviewImageValid(data: Blob): Promise<boolean> {
    if (!(data instanceof Blob) || data.size < 4) {
      return false;
    }

    const header = new Uint8Array(await data.slice(0, 4).arrayBuffer());
    const isJpg = header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
    const isPng = header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47;

    if (!isJpg && !isPng) {
      return false;
    }
    
    return true;
  }

  private _checkActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const parentElement = e.currentTarget.closest('.message-footer') || e.currentTarget.closest('.message-header') || e.currentTarget.closest('.context-divider-card');
    if (!parentElement) return false;
    const opacity = parseFloat(getComputedStyle(parentElement).opacity);
    return opacity > 0.3;
  }

  render() {
    const { message, isGenerating, hasModel, showTokenUsage, codeHighlightingEnabled } = this.props;
    const { currentModel, generatingMessageId } = this.props;
    const { onRemove, onShare, onFork, onRegenerate: onRegenerateWrapped, onRegenerateFull, onMessageSelected, onTakeImageSettings } = this.props;
    const maxSystemSize = window.innerWidth < 600 ? 120 : 240;
    const parent = message.parent;

    const onRegenerate = onRegenerateWrapped ? (msg: Message) => {
      // Clear preview blob URL on regenerate
      if (this._previewBlobUrl) {
        URL.revokeObjectURL(this._previewBlobUrl);
        this._executingNode = null;
        this._previewBlobUrl = null;
      }
      onRegenerateWrapped(msg);
    } : undefined;

    let selfMessage = message;
    let navParent: Message | null | undefined = parent;

    if (message.collapsedMessages) {
      // For collapsed tool-call chains we need the right "branching point" for navigation.
      // • Full-LLM regen creates sibling assistants under the user message  → navigate at user level.
      // • Quick tool-only regen creates sibling tool results under the same assistant → navigate at assistant level.
      const firstAssistant = message.collapsedMessages.find(m => m.role === 'assistant');
      if (
        firstAssistant &&
        (firstAssistant as Message).parent &&
        ((firstAssistant as Message).parent as Message).children &&
        (((firstAssistant as Message).parent as Message).children!.length > 1)
      ) {
        // Multiple assistants under the same parent → user-level navigation (full regenerate case)
        selfMessage = firstAssistant as Message;
        navParent = (firstAssistant as Message).parent as Message;
      } else {
        // Single assistant → fall back to tool-result-level navigation (quick regenerate case)
        const lastToolMessage = [...message.collapsedMessages].reverse().find(m => m.role === 'tool');
        if (lastToolMessage) selfMessage = lastToolMessage;
        // navParent stays as message.parent (= tool-calling assistant, set by collapseThinking logic)
      }
    }

    const navChildren = navParent && navParent.children || [];
    const selfChildNumber = navChildren.findIndex(c => c.id === selfMessage.id) + 1;
    const canGoPrev = selfChildNumber > 1;
    const canGoNext = selfChildNumber < navChildren.length;
    const prevMessage = canGoPrev ? navChildren[selfChildNumber - 2] : null;
    const nextMessage = canGoNext ? navChildren[selfChildNumber] : null;
    const hasSiblings = canGoPrev || canGoNext;

    let userName = window.user?.givenName || window.user?.name || /* get before @ in email if name not available */ window.user?.email?.split('@')[0] || 'You';
    let userImage = window.user?.picture ?? `//www.gravatar.com/avatar/${window.user?.emailMd5}?d=blank`;
    if (message.role === 'user' && typeof this.props.userName !== 'undefined') {
      userName = this.props.userName ?? 'Unknown User';
      userImage = this.props.userPicture ?? (this.props.userEmailMd5 ? `//www.gravatar.com/avatar/${this.props.userEmailMd5}?d=blank` : '');
    }

    const capabilities = currentModel?.capabilities || [];
    const isImageUploadEnabled = capabilities.includes('vision') || capabilities.includes('tools');
    const hasCounter = navParent && navParent.children && navParent.children.length > 1 && onMessageSelected;
    const extraAssistantActionClass = (hasCounter ? ' message-footer--with-counter' : '');

    const messagesIncluded = message.collapsedMessages ? message.collapsedMessages : [message];
    const isThisMessageGenerating = !!isGenerating && messagesIncluded.some(m => m.id === generatingMessageId);
    // Assistant that issued an image OR video generation tool call — the "Use config"
    // button restores that tool's parameters into the composer (ocr excluded).
    const toolWithImageGenerateToolCall = [...messagesIncluded].reverse().find(m => m.role === 'assistant' && m.toolsFunctionName && ((m.toolsFunctionName.includes('image') && !m.toolsFunctionName.includes('ocr')) || m.toolsFunctionName.includes('video')));

    // Image/video generation: drives the placeholder→preview (media) box in the
    // thinking log. Must check BOTH the legacy single-call field (toolsFunctionName,
    // only ever set while tool_calls stream from the LLM) AND the toolCalls array —
    // button-driven generations create the assistant message client-side with only
    // toolCalls set, so checking toolsFunctionName alone hid the box on a fresh run
    // (it appeared only after a full LLM regeneration). The tool message itself is
    // checked too: standalone tool messages (later rounds) have no assistant message
    // in their own messagesIncluded. Video has no preview frame — animated placeholder
    // for the whole run. OCR is excluded (it reads, not generates).
    const isMediaToolName = (name?: string | null) =>
      !!name && ((name.includes('image') && !name.includes('ocr')) || name.includes('video'));
    const isMediaGenMessage = [...messagesIncluded].some(m =>
      m.role === 'tool'
        ? isMediaToolName(m.toolName)
        : m.role === 'assistant' && (isMediaToolName(m.toolsFunctionName) || (m.toolCalls ?? []).some(tc => isMediaToolName(tc.name))));

    // Whether the collapsed message contains exactly one image or video generation tool call.
    // Used to show a quick "Regenerate" (tool-only, no LLM) vs "Regenerate full" (LLM + tool).
    const isImageOrVideoTool = (name: string) => isMediaToolName(name);
    const imageVideoToolCallsInMsg = message.collapsedMessages
      ? message.collapsedMessages.filter(m => m.role === 'assistant' && m.toolsFunctionName && isImageOrVideoTool(m.toolsFunctionName))
      : [];
    const isCollapsedSingleImageVideoCall = !!message.collapsedMessages && imageVideoToolCallsInMsg.length === 1;

    let content = message.content;
    if (message.role === 'user' && codeHighlightingEnabled) {
      //replace \n new 2 spaces and newline for markdown line breaks
      content = content.replace(/\n/g, '  \n');
    }

    // Note: collapseThinking mode groups the in-progress assistant+tool pair into a single
    // message as soon as both exist (collapsedMessages gets set immediately, not just once
    // done), so gating on `!message.collapsedMessages` hid the progress UI for every tool
    // call (image generation included). `toolProgress` is only ever set while a tool is
    // actively running and is cleared in chat:tool_done, so it's the correct live signal.
    const isToolProgress = message.role === 'tool' && !!message.toolProgress && !message.errorMessage;
    // The outer progress block is only shown while the compact log is collapsed.
    // When the log is expanded, the progress lives inside CompactThinkingLog instead.
    // Media (image/video) messages never use the outer block — CompactThinkingLog
    // renders the media box in both summary and full modes (single stable container).
    const showOuterProgress = isToolProgress && !this._logExpanded && !isMediaGenMessage;
    const canRemove = !isGenerating && (message.role === 'user' && !!message.parentId || message.parent?.children?.length && message.parent?.children?.length > 1);

    // Any user can open the full conversation captured for a sub-agent (run_agent / run_skill)
    // tool message — it's their own data, linked via the tool message's completionCallId.
    const showSubAgentLog = message.role === 'tool' && !!message.completionCallId
      && (message.toolName === 'run_agent' || message.toolName === 'run_skill');

    const renderActions = () => {
      return <> 
        {message.role === 'user' && <>
          <button className="action-btn action-btn-copy" onClick={this._onCopyClick}><ImgCopy /> Copy</button>
          {canRemove && onRemove && <button className="action-btn" onClick={(e) => this._checkActionClick(e) && onRemove(message)}><ImgTrash /> Delete</button>}
          {!this.isEditing && hasModel && <button className="action-btn" onClick={this._onEditClick}><ImgEdit /> Edit</button>}
        </>}
        {!!toolWithImageGenerateToolCall && onTakeImageSettings && !isThisMessageGenerating && <>
          <button className={`action-btn ${extraAssistantActionClass}`} onClick={(e) => this._checkActionClick(e) && onTakeImageSettings?.(toolWithImageGenerateToolCall)}><ImgTakeImage /> <span>Use config</span></button>           
        </>}
        {message.role === 'assistant' && <>    
          {!toolWithImageGenerateToolCall && <button className="action-btn action-btn-copy" onClick={this._onCopyClick}><ImgCopy /><span>Copy</span></button>}
          {!isGenerating && hasSiblings && onRemove && <button className={`action-btn ${extraAssistantActionClass}`} onClick={(e) => this._checkActionClick(e) && onRemove(message)}><ImgTrash /> <span>Delete</span></button>}
          {!isGenerating && hasModel && onRegenerate && <button className={`action-btn ${extraAssistantActionClass}`} onClick={(e) => this._checkActionClick(e) && onRegenerate(message)}><ImgRetry /> Regenerate</button>}
          {!isThisMessageGenerating && hasModel && (onShare || onFork || (isCollapsedSingleImageVideoCall && !!onRegenerateFull)) && <>
            <button ref={this._moreActionsBtnRef} className={`action-btn ${extraAssistantActionClass}`} title="More actions">
              <ImgMoreDots />
            </button>
            <ContextMenu container={this._moreActionsBtnRef as React.RefObject<HTMLElement | null>} showOnClick>
              {() => <>
                {isCollapsedSingleImageVideoCall && onRegenerateFull && <ContextMenuItem title={<><ImgRetry size={18} /> Regenerate full</>} onClick={() => onRegenerateFull(message)} />}
                {onShare && <ContextMenuItem title={<><ImgShare /> Share message</>} onClick={() => onShare(message)} />}
                {onFork && <ContextMenuItem title={<><ImgFork /> Fork to new chat</>} onClick={() => onFork(message)} />}
              </>}
            </ContextMenu>
          </>}
          {!this.isEditing && message.role === 'assistant' && <MessageSources sources={message.sources} />}
        </>}
        {message.role === 'tool' && <>
          {!isGenerating && hasSiblings && onRemove && <button className="action-btn" onClick={(e) => this._checkActionClick(e) && onRemove(message)}><ImgTrash /> Delete</button>}
          {!isGenerating && hasModel && onRegenerate && <button className="action-btn" onClick={(e) => this._checkActionClick(e) && onRegenerate(message)}><ImgRetry /> Regenerate</button>}
        </>}
        {message.role === 'system' && <>
          <button className="action-btn action-btn-copy" onClick={this._onCopyClick}><ImgCopy /> Copy</button>
          {!isGenerating && onRemove && <button className="action-btn" onClick={(e) => this._checkActionClick(e) && onRemove(message)}><ImgTrash /> Delete</button>}
          {!isGenerating && !this.isEditing && hasModel && <button className="action-btn" onClick={this._onEditClick}><ImgEdit /> Edit</button>}
        </>}

      </>;
    };

    return (
      <div className={`message ${message.role}`} id={message.id}>
        <div className="message-avatar">
          {message.role === 'user' && <strong title="User">U</strong>}
          {message.role === 'assistant' && <strong title="Assistant">A</strong>}
          {message.role === 'system' && <strong title="System">S</strong>}
          {message.role === 'tool' && <strong title="Tool">T</strong>}
          {message.role === 'user' && !!userImage && <img className="message-avatar-icon" src={userImage} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        </div>
        <div className="message-content">
          <div className="message-header">
            <span className="message-author">
              {message.role === 'user' && userName}
              {message.role === 'assistant' && (message.modelName ? modelService.getModelName(message.modelName) : 'Assistant')}
              {message.role === 'system' && 'System'}
              {message.role === 'tool' && !message.content && 'Tool'}
              {!!message.content && message.role === 'tool' && <>
                <span className="tool-result-title">
                  {`Tool Worked for ${((message.timeTaken || 0) > 100_000_00 ? ((message.timeTaken || 0) / 1_000_000_000).toFixed(1) + 's' : ((message.timeTaken || 0) / 1_000_000).toFixed(1) + 'ms')}`}
                  {message.timestamp && <span className="message-time"><Timestamp timestamp={message.timestamp} /></span>}
                </span>
              </>}
            </span>
            {(typeof message.timeTaken === 'number' && message.timeTaken > 0 || message.role === 'tool' || isThisMessageGenerating) && <MessageStats message={message} isGenerating={isThisMessageGenerating} />}
            {message.role !== 'tool' && message.timestamp && <span className="message-time">
              <Timestamp timestamp={message.timestamp} />
            </span>}
            {message.content.length > 1000 && message.role !== 'context' /* context-menu "⋯" for long messages instead of duplicated inline buttons */ && <div className="message-actions">
              <button ref={this._moreActionsHeaderBtnRef} className="action-btn" title="Message actions">
                <ImgMoreDots />
              </button>
              <ContextMenu container={this._moreActionsHeaderBtnRef as React.RefObject<HTMLElement | null>} showOnClick>
                {() => {
                  // Synthetic event rooted in the header button so _checkActionClick passes opacity guard
                  const synthE = () => ({ currentTarget: this._moreActionsHeaderBtnRef.current } as React.MouseEvent<HTMLButtonElement>);
                  return <>
                    {message.role === 'user' && <>
                      <ContextMenuItem title={<><ImgCopy size={18} /> Copy</>} onClick={() => void this._onCopyClick(synthE())} />
                      {canRemove && onRemove && <ContextMenuItem title={<><ImgTrash size={18} /> Delete</>} onClick={() => onRemove(message)} />}
                      {!this.isEditing && hasModel && <ContextMenuItem title={<><ImgEdit /> Edit</>} onClick={() => void this._onEditClick(synthE())} />}
                    </>}
                    {!!toolWithImageGenerateToolCall && onTakeImageSettings && !isThisMessageGenerating &&
                      <ContextMenuItem title={<><ImgTakeImage /> Use config</>} onClick={() => onTakeImageSettings(toolWithImageGenerateToolCall)} />
                    }
                    {message.role === 'assistant' && <>
                      <ContextMenuItem title={<><ImgCopy size={18} /> Copy</>} onClick={() => void this._onCopyClick(synthE())} />
                      {!isGenerating && hasSiblings && onRemove && <ContextMenuItem title={<><ImgTrash size={18} /> Delete</>} onClick={() => onRemove(message)} />}
                      {!isGenerating && hasModel && onRegenerate && <ContextMenuItem title={<><ImgRetry size={18} /> Regenerate</>} onClick={() => onRegenerate(message)} />}
                      {isCollapsedSingleImageVideoCall && !isGenerating && hasModel && this.props.onRegenerateFull && <ContextMenuItem title={<><ImgRetry size={18} /> Regenerate full</>} onClick={() => this.props.onRegenerateFull!(message)} />}
                      {!isThisMessageGenerating && onShare && <ContextMenuItem title={<><ImgShare /> Share</>} onClick={() => onShare(message)} />}
                      {!isThisMessageGenerating && onFork && <ContextMenuItem title={<><ImgFork /> Fork to new chat</>} onClick={() => onFork(message)} />}
                    </>}
                    {message.role === 'tool' && <>
                      {!isGenerating && hasSiblings && onRemove && <ContextMenuItem title={<><ImgTrash size={18} /> Delete</>} onClick={() => onRemove(message)} />}
                      {!isGenerating && hasModel && onRegenerate && <ContextMenuItem title={<><ImgRetry size={18}  /> Regenerate</>} onClick={() => onRegenerate(message)} />}
                      {showSubAgentLog && <ContextMenuItem title={<><ImgSubAgent size={18} /> Sub-agent</>} onClick={() => void this._onViewCompletionLog(message, 'chat')} />}
                    </>}
                    {message.role === 'system' && <>
                      <ContextMenuItem title={<><ImgCopy size={18} /> Copy</>} onClick={() => void this._onCopyClick(synthE())} />
                      {!isGenerating && onRemove && <ContextMenuItem title={<><ImgTrash size={18} /> Delete</>} onClick={() => onRemove(message)} />}
                      {!isGenerating && !this.isEditing && hasModel && <ContextMenuItem title={<><ImgEdit /> Edit</>} onClick={() => void this._onEditClick(synthE())} />}
                    </>}
                  </>;
                }}
              </ContextMenu>
            </div>}
          </div>
          {/* Compact thinking log: always shown for messages with thinking/tool calls */}
          <CompactThinkingLog
            chatId={this.props.chatId}
            message={message}
            collapsedMessages={message.collapsedMessages}
            isGenerating={!!isGenerating}
            hasModel={!!hasModel}
            generatingMessageId={generatingMessageId}
            onViewToolResult={this._onViewToolResult}
            onRegenerate={onRegenerate}
            onViewSubAgentLog={(m) => void this._onViewCompletionLog(m, 'chat')}
            executingNode={this._executingNode}
            previewBlobUrl={this._previewBlobUrl}
            progress={this._progress}
            promptId={this._promptId}
            queuePosition={this._queuePosition}
            isImageGen={isMediaGenMessage}
            onCancel={this._onCancelClick}
            onExpandedChange={this._onLogExpandedChange}
            showFullThinking={this.props.showFullThinking}
            enableFormatting={this.props.codeHighlightingEnabled}
          />
          <ChatMessageToolApproval message={message} pendingToolApprovalName={this.props.pendingToolApprovalName} toolApprovalDecision={this.props.toolApprovalDecision} onToolApprovalDecision={this.props.onToolApprovalDecision} />
          {message.role === 'context' && (
            <ContextDividerCard
              message={message}
              generatingMessageId={generatingMessageId}
              isGenerating={!!isGenerating}
              hasModel={!!hasModel}
              onRegenerate={onRegenerate}
              onRemove={onRemove}
              onCopy={this._onCopyClick}
              onViewSource={this._openCompactedDialog}
            />
          )}
          {isGenerating && isThisMessageGenerating && <ChatMessageModelLoadingProgress message={message} />}
          {message.role !== 'context' && !this.isEditing && (!!message.content || !!message.thinking || showOuterProgress || !!message.errorMessage || !!message.isAborted) && <div className="message-text">
            {(!!message.content || !!message.thinking) && message.role !== 'tool' && <LazyMarkdownRenderer 
              content={content} 
              maxLength={message.role === 'system' ? maxSystemSize : 0} 
              isGenerating={isThisMessageGenerating} 
              enableFormatting={codeHighlightingEnabled}
              attachmentMetas={message.attachmentMetas}
              isThisMessageGenerating={isThisMessageGenerating} // Force re-render on new content during generation
              chatImageFileList={this.props.chatImageFileList}
              onUseImageAttachment={this.props.onUseImageAttachment}
            />}
            {showOuterProgress && <ChatMessageProgress message={message} executingNode={this._executingNode} queuePosition={this._queuePosition} onCancel={this._onCancelClick} enableFormatting={codeHighlightingEnabled} />}
            {message.errorMessage && <div className="message-error">{message.errorMessage}</div>}
            {message.isAborted && !message.errorMessage && <div className="message-aborted">Request was aborted</div>}
          </div>}
          {!this.isEditing && <div className="message-attachments">
            {(message.attachmentIds || []).filter((id) => !this._isImageInContent(message.content, id)).map((id) => this._renderAttachment(id))}
          </div>}
          {!this.isEditing && (message.id !== this.props.generatingMessageId) && <div className="message-footer">
            {hasCounter && message.role !== 'context' && navParent?.children && <span className="message-children-count">
              <button disabled={!canGoPrev || !prevMessage} title="Previous message" onClick={() => prevMessage && onMessageSelected(prevMessage)}>
                <ImgPrevious />
              </button>
              <span>{selfChildNumber}/{navParent.children.length}</span>
              <button disabled={!canGoNext || !nextMessage} title="Next message" onClick={() => nextMessage && onMessageSelected(nextMessage)}>
                <ImgNext />
              </button>
            </span>}
            {renderActions()}
            {(message.role === 'assistant' || message.role === 'tool') && <MessageCost message={message} />}
          </div>}
          {this.isEditing && <>
            <textarea
              ref={this._textareaRef}
              autoFocus
              rows={1}
              className="message-edit"
              value={this.editedContent}
              onChange={(e) => this._onEditChange(e)}
              onInput={this._recalculateHeight}
              onKeyDown={this._onPromptKeyDown}              
              onPaste={isImageUploadEnabled ? this._onPaste : undefined}
            />
            <div className="edit-actions">
              <button className="btn btn-primary" disabled={this._uploading.isPending} title={this._uploading.isPending ? 'Uploading attachments…' : undefined} onClick={this._onEditSaveClick}>Save</button>
              <button className="btn btn-secondary" onClick={this._onEditCancelClick}>Cancel</button>
              
              {showTokenUsage && <TokensCounter 
                isGenerating={!!isGenerating}
                inputValue={this.editedContent}
                hasImage={this._existingImageMetas.length > 0} 
                currentModel={this.props.currentModel} 
                tools={this.props.tools}
              />}

              {this._hasKeyboard && <div className="key-help">
                  <span className={this._isShiftPressed || this._isCtrlPressed ? 'help-enabled' : ''}>Shift <ImgKeyArrow size={12} /> = New line</span>
              </div>}
            </div>
            {isImageUploadEnabled && <div className="edit-images-list">
              <input type="file" ref={this._fileRef} accept="image/png,image/jpeg,image/gif,image/webp" multiple style={{ display: 'none' }} onChange={this._onFileUploadChange} />
              <input type="file" ref={this._docFileRef} accept=".pdf,.doc,.docx,.txt,.md,.csv,.js,.ts,.jsx,.tsx,.py,.java,.cpp,.c,.h,.cs,.go,.rb,.php,.html,.css,.xml,.svg,.json,.yaml,.yml,.sql,.log,.sh" multiple style={{ display: 'none' }} onChange={this._onDocFileUploadChange} />
              <button ref={this._attachBtnRef} className="attach-btn" title="Attach File">
                <ImgAttachFile />
                Attach File
              </button>
              <ContextMenu showOnClick container={this._attachBtnRef}>
                {() => (<>
                  <ContextMenuItem title={<><ImgFileGeneric size={14} /> Attach Image</>} onClick={this._onAttachImageClick} />
                  <ContextMenuItem title={<><ImgFileGeneric size={14} /> Attach Document</>} onClick={this._onAttachDocClick} />
                </>)}
              </ContextMenu>
              {this._existingImageMetas.length > 0 && <div className="attached-images">
                {this._existingImageMetas.map((meta, index) => (
                  <div key={`existing-img-${meta.id}`} className="attached-image">
                    <ImageWrapper
                      src={`/image/${meta.id}`}
                      className="attached-image-preview"
                      alt={meta.filename || 'Attached Image'}
                    />
                    <button className="remove-btn" onClick={(e) => { e.stopPropagation(); this._onRemoveExistingImage(index); }} title="Remove Image">&#x2715;</button>
                  </div>
                ))}
              </div>}
              {this._pendingUploads.filter(p => p.isImage).map(p => (
                <span key={p.id} className="attached-image attached-image-uploading" title={`Uploading ${p.filename}… ${Math.round(p.progress * 100)}% (click to cancel)`} onClick={() => void this._onCancelUploadClick(p.id)}>
                  <UploadProgressRing progress={p.progress} size={30} />
                  <button className="remove-btn" title="Stop uploading" onClick={(e) => { e.stopPropagation(); void this._onCancelUploadClick(p.id); }}>&#x2715;</button>
                </span>
              ))}
              {this._existingAttachmentMetas.filter(m => !m.isImage).map((meta, i) => {
                const isTextFragment = meta.filename.startsWith('text-fragment-') && meta.filename.endsWith('.txt');

                if (isTextFragment) {
                  const text = meta.textContent ? meta.textContent.trim().substring(0, 80) + (meta.textContent.length > 80 ? '...' : '') : '';
                  return (
                    <div key={`existing-${meta.id}`} className="attached-text-fragment">
                      <span className="text-fragment-preview viewable" onClick={() => this._onViewExistingAttachment(meta)} title="Click to view">
                        {text || 'Pasted text'}
                      </span>
                      {meta.textContent && <span className="text-fragment-size">{this._formatSize(meta.size)}</span>}
                      <button className="remove-btn" onClick={() => this._onRemoveExistingAttachment(i)} title="Remove"><ImgCancel size={14} /></button>
                    </div>
                  );
                }

                const attachmentType = getAttachmentType(meta.contentType, meta.filename);

                if (attachmentType === 'pdf') {
                  return (
                    <Suspense key={`existing-${meta.id}`} fallback={<span className="attachment-pdf-card attachment-pdf-loading">Loading…</span>}>
                      <PdfAttachmentCard
                        source={{ url: `/image/${meta.id}` }}
                        filename={meta.filename}
                        sizeStr={this._formatSize(meta.size)}
                        onView={() => this._onViewExistingAttachment(meta)}
                        onRemove={() => this._onRemoveExistingAttachment(i)}
                        editMode
                      />
                    </Suspense>
                  );
                }

                const isViewable = attachmentType === 'text';
                const iconType = getAttachmentIconComponent(meta.contentType, meta.filename);
                const IconComponent = resolveIconComponent(iconType);
                
                return (
                  <div key={`existing-${meta.id}`} className="attached-file">
                    <span className="attached-file-icon"><IconComponent size={18} /></span>
                    <span 
                      className={"attached-file-name" + (isViewable ? " viewable" : "")}
                      onClick={isViewable ? () => this._onViewExistingAttachment(meta) : undefined}
                      title={isViewable ? "Click to view" : meta.filename}
                    >
                      {meta.filename}
                    </span>
                    <button className="remove-btn" onClick={() => this._onRemoveExistingAttachment(i)} title="Remove"><ImgCancel size={14} /></button>
                  </div>
                );
              })}
              {this._pendingUploads.filter(p => !p.isImage).map(p => (
                <span key={p.id} className="attached-file attached-file-uploading" title={`Uploading ${p.filename}… ${Math.round(p.progress * 100)}% (click to cancel)`} onClick={() => void this._onCancelUploadClick(p.id)}>
                  <UploadProgressRing progress={p.progress} size={16} />
                  <span className="attached-file-name">{p.filename}</span>
                  <button className="remove-btn" title="Stop uploading" onClick={(e) => { e.stopPropagation(); void this._onCancelUploadClick(p.id); }}><ImgCancel size={14} /></button>
                </span>
              ))}
            </div>}
          </>}
        </div>
      </div>
    );
  }

  @action
  private _onAttachImageClick = () => {
    if (this._fileRef.current) {
      this._fileRef.current.click();
    }
  }

  @action
  private _onAttachDocClick = () => {
    if (this._docFileRef.current) {
      this._docFileRef.current.click();
    }
  }

  @action.bound
  private async _onViewExistingAttachment(meta: AttachmentMeta) {
    try {
      const attachmentType = meta.type || getAttachmentType(meta.contentType, meta.filename);
      if (attachmentType === 'pdf') {
        await modalService.show(PdfViewerDialog, {
          attachmentId: meta.id,
          filename: meta.filename
        });
      } else {
        // Fetch full text content from server
        const response = await fetch(`/image/${meta.id}`);
        if (!response.ok) throw new Error('Failed to fetch attachment');
        
        const blob = await response.blob();
        const text = await blob.text();
        
        await modalService.show(TextViewerDialog, {
          attachmentId: meta.id,
          filename: meta.filename,
          content: text,
          model: this.props.currentModel?.name
        });
      }
    } catch (error) {
      console.error('Failed to view attachment:', error);
      alert('Failed to view attachment content.');
    }
  }

  @action.bound
  async _onViewToolResult(message: Message) {
    let content = message.content;

    if (message.contentTruncated && message.id) {
      const resp = await fetch(`/api/messages/${message.id}/content`);
      if (resp.ok) {
        const data = await resp.json() as { content: string };
        content = data.content;
      } else {
        throw new Error(`Failed to fetch full content from server for tool result ${message.id}`);
      }
    }

    if (!content) {
      alert('No tool result available to view.');
      return;
    }

    await modalService.show(TextViewerDialog, {
      attachmentId: '',
      filename: 'Tool Result',
      content,
      model: this.props.currentModel?.name
    });
  }

  @action.bound
  private async _onViewCompletionLog(message: Message, initialSection?: 'request' | 'response' | 'chat') {
    const callId = message.completionCallId;
    if (!callId) return;
    try {
      const detail = await apiService.get<CompletionLogDetail>(`/api/completion-log/by-call/${callId}`, { suppress404: true });
      await modalService.show(CompletionLogDetailDialog, { detail, initialSection });
    } catch (error) {
      alert('Failed to load completion log: ' + error);
    }
  }

  @action.bound
  private async _onViewMessageAttachment(attachmentId: string, filename: string, contentType: string) {
    try {
      const attachmentType = getAttachmentType(contentType, filename);
      
      if (attachmentType === 'pdf') {
        await modalService.show(PdfViewerDialog, {
          attachmentId: attachmentId,
          filename: filename
        });
      } else {
        // Fetch full text content from server
        const response = await fetch(`/image/${attachmentId}`);
        if (!response.ok) throw new Error('Failed to fetch attachment');
        
        const blob = await response.blob();
        const text = await blob.text();
        
        await modalService.show(TextViewerDialog, {
          attachmentId: attachmentId,
          filename: filename,
          content: text,
          model: this.props.currentModel?.name
        });
      }
    } catch (error) {
      console.error('Failed to view attachment:', error);
      alert('Failed to view attachment content.');
    }
  }

  @action
  private _onRemoveExistingImage = (index: number) => {
    this._existingImageMetas.splice(index, 1);
  }

  @action
  private _onRemoveExistingAttachment = (index: number) => {
    this._existingAttachmentMetas.splice(index, 1);
  }
  
  @action
  _onFileUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._fileRef.current!.value = '';
      return;
    }

    for (const file of Array.from(files)) {
      if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
        continue;
      }
      if (imageService.isImageFile(file)) {
        await this._processImageFile(file);
      }
    }
    this._fileRef.current!.value = '';
  }

  @action
  _onDocFileUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._docFileRef.current!.value = '';
      return;
    }

    for (const file of Array.from(files)) {
      if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
        continue;
      }
      const meta = await this._uploadFile(file);
      if (meta) this._existingAttachmentMetas.push(meta);
    }
    this._docFileRef.current!.value = '';
  }
  
  /**
   * Upload a single file and track it in flight: shows a placeholder while the request
   * is running (loader = `_uploading`) and surfaces failures as a toast. Returns the
   * created attachment meta, or null when the upload failed.
   */
  @action
  private async _uploadFile(file: File): Promise<AttachmentMeta | null> {
    const pendingId = `p-${++this._uploadSeq}`;
    const controller = new AbortController();
    this._uploadAborts.set(pendingId, controller);
    this._pendingUploads.push({ id: pendingId, filename: file.name, isImage: imageService.isImageFile(file), progress: 0 });
    try {
      const meta = await imageService.uploadAttachment(file, this._uploading, controller.signal, fraction => {
        runInAction(() => {
          const pending = this._pendingUploads.find(p => p.id === pendingId);
          if (pending) pending.progress = fraction;
        });
      });
      this._removePendingUpload(pendingId);
      return meta;
    } catch (error) {
      this._removePendingUpload(pendingId);
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null; // user cancelled — already confirmed by them
      }
      toastService.error('Upload failed', `${file.name}: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    } finally {
      this._uploadAborts.delete(pendingId);
    }
  }

  @action
  private _removePendingUpload(pendingId: string) {
    this._pendingUploads = this._pendingUploads.filter(p => p.id !== pendingId);
  }

  /** Click on an in-flight placeholder: confirm, then abort the upload in flight. */
  @action
  private async _onCancelUploadClick(pendingId: string) {
    if (!await modalService.showConfirmation('Stop uploading this attachment?', 'Cancel Upload')) return;
    this._uploadAborts.get(pendingId)?.abort();
    this._removePendingUpload(pendingId);
    this._uploadAborts.delete(pendingId);
  }

  @action
  private _processImageFile = async (file: File): Promise<void> => {
    const meta = await this._uploadFile(file);
    if (meta) this._existingImageMetas.push(meta);
  };

  @action
  private _onCancelClick = async () => {
    const operationId = this.props.message.toolProgress?.operationId || this._promptId;
    if (!operationId) {
      return;
    }

    const isSearchOperation = !!this.props.message.toolProgress?.operationId;
    const confirmMessage = isSearchOperation 
      ? "Are you sure you want to cancel the search?" 
      : "Are you sure you want to cancel the image generation?";
    const confirmTitle = isSearchOperation ? "Cancel Search" : "Cancel Image Generation";

    if (await modalService.showConfirmation(confirmMessage, confirmTitle)) {
      await this.props.onImageCancel?.(this.props.message, operationId);
    }
  }

  @action
  private _onEditClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!this._checkActionClick(e)) return;
    this.editedContent = this.props.message.content;
    this.isEditing = !this.isEditing;
    this.props.onEditing?.(this.props.message, this.isEditing);
    if (this.isEditing) {
      // Focus the textarea when entering edit mode
      setTimeout(() => {
        const textarea = document.querySelector('.message-edit') as HTMLTextAreaElement;
        if (textarea) {
          this._recalculateHeight();
          textarea.focus();
          textarea.setSelectionRange(0, textarea.value.length); // Select all text
        }
      }, 0);
    }

    // Load existing attachments (by id — metas fetched, no base64 round-trip)
    const { message: { attachmentIds } } = this.props;
    this._existingImageMetas = [];
    this._existingAttachmentMetas = [];

    if (attachmentIds && attachmentIds.length > 0) {
      for (const id of attachmentIds) {
        const meta = await imageService.getAttachmentMeta(id);
        if (meta.isImage) {
          this._existingImageMetas.push(meta);
        } else {
          this._existingAttachmentMetas.push(meta);
        }
      }
    }
  }

  @action 
  private _onEditChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.editedContent = e.target.value;
    this._recalculateHeight();
  }

  @action
  private _onEditSaveClick = () => {
    this.isEditing = false;    
    this.props.onEditing?.(this.props.message, this.isEditing);

    //if empty system message - remove it
    const { message } = this.props;
    if (message.role === 'system' && !this.editedContent) {
      this.props.onRemove?.(message);
    } else {
      // Collect attachment IDs to bind to the new message — images + docs. Newly
      // uploaded drafts transfer their reference; ids already bound to other
      // messages get an extra reference (bind case 2).
      const attachmentIds = [
        ...this._existingImageMetas.map(m => m.id),
        ...this._existingAttachmentMetas.map(m => m.id),
      ];
      this.props.onUpdated?.(message, this.editedContent, attachmentIds);
    }
  }

  @action
  private _onEditCancelClick = () => {
    this.isEditing = false;
    this.props.onEditing?.(this.props.message, this.isEditing);

    //if empty system message - remove it
    const { message } = this.props;
    if (message.role === 'system' && !this.editedContent) {
      this.props.onRemove?.(message);
    }
  }

  private _onCopyClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!this._checkActionClick(e)) return;
    const { message, codeHighlightingEnabled } = this.props;
    const btn = e.currentTarget;
    
    try {
      if (codeHighlightingEnabled && message.role !== 'tool') {
        // Lazy-load markdown-to-HTML converter and convert
        const { markdownToHtml } = await import('../utils/markdownToHtml.ts');
        const htmlContent = await markdownToHtml(message.content);
        
        // Create clipboard item with both HTML and plain text
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([message.content], { type: 'text/plain' });
        
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob
          })
        ]);
      } else {
        // Copy as plain text
        await navigator.clipboard.writeText(message.content);
      }
      
      btn.classList.add('copied');
      setTimeout(() => {
        btn.classList.remove('copied');
      }, 1000);
    } catch (err) {
      btn.classList.add('failed');
      btn.title = 'Failed to copy message: ' + err;
      setTimeout(() => {
        btn.classList.remove('failed');
        btn.title = '';
      }, 10000);
    }
  };

  @action
  _onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      this._onEditCancelClick();
    }
    
    if (e.key === 'Enter') {
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        this._onEditSaveClick();
        return;
      }

      if (e.ctrlKey){
        const textarea = this._textareaRef.current!;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        // Insert newline at cursor
        const before = this.editedContent.substring(0, start);
        const after = this.editedContent.substring(end);
        this.editedContent = before + "\n" + after;

        this._textareaRef.current!.value = this.editedContent;
        textarea.selectionStart = textarea.selectionEnd = start + 1;
      }
      this._recalculateHeight();
    }
  };
  
  @action
  private _recalculateHeight = () => {
      this._textareaRef.current!.style.height = 'auto';
      const newHeight = Math.min(192, Math.max(48, this._textareaRef.current!.scrollHeight));
      this._textareaRef.current!.style.height = newHeight + 'px';
  };
  
  @action
  _onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    if (this.props.message.role === 'system'){
      return;
    }

    const text = clipboardData.getData('text/plain');
    e.preventDefault();
    // Shift = paste as plain text (never an attachment). Prefer the paste event's own
    // shiftKey (accurate in Chromium/WebKit, no stuck-state risk); fall back to the
    // global flag only for Firefox, whose ClipboardEvent does not expose shiftKey.
    const eventShiftKey = (e.nativeEvent as unknown as KeyboardEvent).shiftKey;
    const forcePlain = eventShiftKey !== undefined ? eventShiftKey : this._isShiftPressed;
    const result = await imageService.getAttachmentsFromDataTransfer(clipboardData, forcePlain);
    if (result.files.length) {
      for (const file of result.files) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
          continue;
        }
        const meta = await this._uploadFile(file);
        if (!meta) continue;
        if (meta.isImage) {
          this._existingImageMetas.push(meta);
        } else {
          this._existingAttachmentMetas.push(meta);
        }
      }
      return;
    } 
    
    if (result.textFragment) {
      const textFile = imageService.createTextFragmentFile(result.textFragment);
      const meta = await this._uploadFile(textFile);
      if (meta) this._existingAttachmentMetas.push(meta);
      return;
    }

    // Regular text paste - use execCommand for proper undo history and cursor placement
    if (text) {
      const textarea = this._textareaRef.current!;
      const inserted = document.execCommand('insertText', false, text);
      if (inserted) {
        this.editedContent = textarea.value;
        this._recalculateHeight();
      } else {
        // Fallback for environments where execCommand is unavailable
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = this.editedContent.substring(0, start);
        const after = this.editedContent.substring(end);
        this.editedContent = before + text + after;
        setTimeout(() => {
          textarea.setSelectionRange(start + text.length, start + text.length);
          this._recalculateHeight();
        }, 0);
      }
    }
  };
  
  @action
  private _loadAttachmentMetas = async () => {
    const { message } = this.props;
    
    // Pre-populate from server-provided attachment metas (avoids extra fetch calls)
    if (message.attachmentMetas) {
      for (const meta of message.attachmentMetas) {
        if (!this._attachmentMetas.has(meta.id)) {
          const typedMeta = { ...meta, type: getAttachmentType(meta.contentType, meta.filename) };
          this._attachmentMetas.set(meta.id, typedMeta);
        }
      }
    }

    const attachmentIds = message.attachmentIds || [];
    for (const id of attachmentIds) {
      if (this._attachmentMetas.has(id)) continue;
      try {
        const response = await fetch(`/image/${id}/meta`);
        if (response.ok) {
          const meta = await response.json() as AttachmentMeta;
          meta.type = getAttachmentType(meta.contentType, meta.filename);
          runInAction(() => {
            this._attachmentMetas.set(id, meta);
          });
        } else {
          console.warn(`Failed to load metadata for attachment ${id}: HTTP ${response.status}`);
        }
      } catch (error) {
        console.warn(`Failed to load metadata for attachment ${id}:`, error);
      }
    }
  };

  // Skip gallery images already rendered inline in the message markdown.
  private _isImageInContent(content: string | undefined, id: string): boolean {
    if (!content) return false;
    return new RegExp(`!\\[[^\\]]*\\]\\(/image/${id}\\)`).test(content);
  }

  private _renderAttachment = (id: string) => {
    const meta = this._attachmentMetas.get(id);

    // If it's an image or we don't have meta yet, render as image with dimension placeholder
    if (!meta || meta.isImage) {
      const width = meta?.imageWidth;
      const height = meta?.imageHeight;
      const style: React.CSSProperties = {};

      if (width && height) {
        const scale = Math.min(1, 300 / Math.max(width, height));
        style.width = `${width * scale}px`;
        style.height = `${height * scale}px`;
      }

      // Use the chat-wide image list if provided, otherwise fall back to message-level
      let fileList = this.props.chatImageFileList;
      if (!fileList) {
        const imageIds = (this.props.message.attachmentIds || []).filter(aid => {
          const m = this._attachmentMetas.get(aid);
          return !m || m.isImage;
        });
        fileList = imageIds.length > 1 ? imageIds.map(aid => `/image/${aid}`) : undefined;
      }

      return (
        <span key={id} className="attachment-image-container" style={style}>
          <ImageWrapper
            id={id}
            className="message-image"
            src={`/image/${id}`}
            alt={meta?.filename || 'Attached image'}
            fileList={fileList}
          />
          {!meta && <span className="attachment-loading">Loading...</span>}
          {this.props.onUseImageAttachment && (
            <button
              className="attachment-use-as-ref-btn"
              title="Use as reference image"
              onClick={() => this.props.onUseImageAttachment!(id)}
            >
              &#x2197;
            </button>
          )}
        </span>
      );
    }

    // Video attachment
    if (meta.type === 'video') {
      return (
        <span key={id} className="attachment-video-container">
          <video
            src={`/attachment/${id}`}
            controls
            controlsList="nodownload"
            className="generated-video"
            preload="metadata"
            style={{ width: '100%', maxWidth: '640px', borderRadius: '8px', display: 'block', backgroundColor: '#000' }}
          />
        </span>
      );
    }

    // Audio attachment
    if (meta.type === 'audio') {
      return (
        <span key={id} className="attachment-audio-container">
          <span className="attachment-audio-name"><ImgMusic size={14} /> {meta.filename}</span>
          <audio
            src={`/attachment/${id}`}
            controls
            controlsList="nodownload"
            preload="metadata"
            style={{ display: 'block', marginTop: 4, maxWidth: '400px' }}
          />
        </span>
      );
    }

    // PDF attachment — render first-page thumbnail card
    if (meta.type === 'pdf') {
      return (
        <Suspense key={id} fallback={<span className="attachment-pdf-card attachment-pdf-loading">Loading…</span>}>
          <PdfAttachmentCard
            source={{ url: `/image/${id}` }}
            filename={meta.filename}
            sizeStr={this._formatSize(meta.size)}
            attachmentId={id}
            onView={() => this._onViewMessageAttachment(id, meta.filename, meta.contentType)}
          />
        </Suspense>
      );
    }

    // Non-image attachment (document, text)
    const iconType = getAttachmentIconComponent(meta.contentType, meta.filename);
    const sizeStr = this._formatSize(meta.size);
    const isViewable = meta.type === 'text';
    
    // Check if this is a pasted text fragment
    const isTextFragment = meta.filename.startsWith('text-fragment-') && meta.filename.endsWith('.txt');

    const IconComponent = resolveIconComponent(iconType);

    return (
      <span key={id} className={`attachment-document attachment-type-${meta.type}`}>
        <span className="attachment-icon"><IconComponent size={20} /></span>
        <span 
          className={"attachment-info" + (isViewable ? " viewable" : "")}
          onClick={isViewable ? () => this._onViewMessageAttachment(id, meta.filename, meta.contentType) : undefined}
          title={isViewable ? "Click to view full content" : meta.filename}
        >
          {!isTextFragment && <span className="attachment-filename" title={meta.filename}>{meta.filename}</span>}
          {!isTextFragment && <span className="attachment-size">{sizeStr}</span>}
          {meta.textContent && (
            <>
              <span className="attachment-preview" title="Click to view full content">
                {isTextFragment ? meta.textContent.substring(0, 400) : meta.textContent.substring(0, 300)}{meta.textContent.length > (isTextFragment ? 400 : 300) ? '...' : ''}
              </span>
              {isTextFragment && <span className="attachment-size">{sizeStr}</span>}
            </>
          )}
          {!isViewable && (
            <a 
              href={`/image/${id}`} 
              download={meta.filename}
              className="attachment-download"
              onClick={(e) => e.stopPropagation()}
              title="Download file"
            >
              Download
            </a>
          )}
        </span>
      </span>
    );
  };

  private _formatSize(size: number) {
    const sizeStr = size < 1024
      ? `${size} B`
      : size < 1024 * 1024
        ? `${(size / 1024).toFixed(1)} KB`
        : `${(size / (1024 * 1024)).toFixed(1)} MB`;

    return sizeStr;
  }
}
